import { extendedFetch } from '@akiyamka/extended-fetch';
import { ApiProgressEvent } from './api-progress-event';
import { unsafeMakeApiError } from './api-untyped-exceptions';
import { getRequesterRequestConfig } from './global-instance';
import { applyRequestInterceptors, applyResponseInterceptors } from './interceptors';
import { parseUrl } from './parse-url';
function extractHeaders(headers) {
    const allHeaders = {};
    const customHeaders = {};
    let customHeadersCount = 0;
    headers.forEach((value, key) => {
        allHeaders[key] = value;
        // Custom header must be included in "Access-Control-Expose-Headers" header
        const name = key.toLocaleLowerCase();
        if (name.startsWith('x-audienzz')) {
            customHeadersCount++;
            customHeaders[name] = value;
        }
    });
    return { allHeaders, customHeaders: customHeadersCount ? customHeaders : null };
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseBody(config) {
    if (['HEAD', 'GET'].includes(config.method.toUpperCase())) {
        return undefined;
    }
    if (!config.data) {
        return undefined;
    }
    if (typeof config.data === 'string') {
        return config.data;
    }
    if (config.requestType === 'json') {
        return JSON.stringify(config.data);
    }
    if (config.requestType === 'form-data') {
        return config.data;
    }
    throw new Error('Unsupported request type');
}
// temporary hack until we implement proper sulution
function jsonParse(text) {
    try {
        return JSON.parse(text, (key, value) => (value === null ? undefined : value));
    }
    catch {
        return text;
    }
}
function getDeserializer(type) {
    switch (type) {
        case 'json':
            return async (resp) => {
                const text = await resp.text();
                if (text === '') {
                    return null;
                }
                return jsonParse(text);
            };
        case 'blob':
            return async (resp) => await resp.blob();
        case 'text':
            return async (resp) => {
                const text = await resp.text();
                return text;
            };
        case 'void':
            return async () => null;
        default:
            // eslint-disable-next-line no-console
            console.error('Unsupported response type');
            return async (resp) => {
                const text = await resp.text();
                return text;
            };
    }
}
export async function makeRequest(config) {
    const globalConfig = getRequesterRequestConfig();
    const preUrl = parseUrl(config.url, config.baseURL ?? globalConfig.baseURL, config.params);
    const preConfig = {
        headers: config.headers ?? {},
        signal: config.signal,
        url: preUrl.pathname,
        data: config.data,
        baseURL: preUrl.origin,
        params: preUrl.searchParams,
        responseTypes: config.responseTypes,
        withCredentials: config.withCredentials ?? false,
        method: config.method ?? 'GET',
        onUploadProgress: config.onUploadProgress,
        requestType: config.requestType,
        ignoreErrors: config.ignoreErrors,
    };
    const totalConfig = await applyRequestInterceptors(preConfig);
    const { params, method, baseURL, headers, withCredentials, signal, url, responseTypes } = totalConfig;
    const totalUrl = parseUrl(url, baseURL, params);
    if (totalConfig.requestType === 'form-data') {
        // Browser will set content type automatically if we sent FormData
        delete headers['Content-Type'];
    }
    const { onUploadProgress } = totalConfig;
    const requestInit = {
        method,
        headers,
        signal,
        body: parseBody(totalConfig),
        credentials: withCredentials ? 'include' : 'omit',
    };
    const response = onUploadProgress
        ? await extendedFetch(totalUrl.toString(), requestInit, {
            onUploadProgress: (event) => onUploadProgress(new ApiProgressEvent({ progress: event.progress })),
        })
        : await fetch(totalUrl.toString(), requestInit);
    if (response.status >= 400) {
        const text = await response.text();
        const data = jsonParse(text);
        const error = /* #__PURE__ */ unsafeMakeApiError({
            response: {
                data,
                status: response.status,
            },
            request: {
                response: text,
            },
            status: response.status,
            message: response.statusText,
        });
        if (!totalConfig.ignoreErrors?.includes(response.status)) {
            applyResponseInterceptors(Promise.reject(error)).catch(() => { });
        }
        throw error;
    }
    let data;
    let resolvedResponseType;
    if (typeof responseTypes === 'string') {
        resolvedResponseType = responseTypes;
    }
    else {
        const mappedType = responseTypes[response.status];
        if (!mappedType) {
            // eslint-disable-next-line no-console
            console.error('Unsupported response type for method', method, 'and status', response.status);
            resolvedResponseType = 'json';
        }
        else {
            resolvedResponseType = mappedType;
        }
    }
    data = await getDeserializer(resolvedResponseType)(response);
    if (data == null) {
        if (resolvedResponseType !== 'void') {
            // Backend returned empty response when data was expected — this violates the API contract
            const error = /* #__PURE__ */ unsafeMakeApiError({
                response: {
                    data: { message: 'EMPTY_RESPONSE' },
                    status: 502,
                },
                request: {
                    response: '',
                },
                status: 502,
                message: `Unexpected empty response from ${method} ${totalUrl}`,
            });
            applyResponseInterceptors(Promise.reject(error)).catch(() => { });
            throw error;
        }
        data = {};
    }
    const { allHeaders, customHeaders } = extractHeaders(response.headers);
    return applyResponseInterceptors(Promise.resolve({
        data: {
            data,
            responseCode: response.status,
            headers: customHeaders,
        },
        headers: allHeaders,
        status: response.status,
        statusText: response.statusText,
    }));
}
